import React, { FC } from 'react';
import { hoverOpacity, ImageContainer } from 'src/shared/globals';
import MdCheckCircle from 'src/shared/material_icons/action/check_circle.svg';
import NoImagePng from 'assets/images/others/no-image.png';
import styled from 'styled-components';
import { cutString, T } from '../../../shared/helpers';
import { Badge } from '../badge';
import { GhostButton } from '../buttons';
import { Icon } from '../icon';
import { CatalogListItemButton, CatalogListItemModel } from './catalogListItemModel';

const Container = styled.div<{ $isDisabled?: boolean; $isNotSelectable?: boolean; $readOnly?: boolean }>`
    max-width: 100%;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    position: relative;
    cursor: ${(props) => (props.$readOnly ? 'default' : props.$isDisabled || props.$isNotSelectable ? 'not-allowed' : 'pointer')};

    ${ImageContainer} {
        margin-bottom: 10px;
        aspect-ratio: 1;

        img {
            ${hoverOpacity}
        }
    }

    ${(props) =>
        props.$isNotSelectable &&
        `
        cursor: not-allowed;
        ${ImageContainer} img {
            cursor: not-allowed;
            &:hover { opacity: 1; }
        }
    `}

    ${(props) => props.$isDisabled && `opacity: 0.3`};
`;

const CheckIcon = styled(Icon)`
    position: absolute;
    right: 10px;
    top: 10px;
    width: 24px;
    height: 24px;
    color: ${(props) => props.theme.app.secondary};
    stroke-width: 0;
`;

const CatalogListItemFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const CatalogListItemFooterText = styled.div<{ $isConfiguratedInZakeke?: boolean; $hideAlreadyConfiguredBadge?: boolean }>`
    display: flex;
    flex-direction: column;
    overflow: hidden;

    span {
        font-size: 12px;
        font-family: ${(props) => props.theme.app.font};
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        cursor: ${(props) => (props.$isConfiguratedInZakeke && !props.$hideAlreadyConfiguredBadge ? 'not-allowed' : 'default')};
    }
`;

const FooterButtons = styled.div`
    display: flex;
    margin: 0;
    padding: 0;
    align-items: center;
`;

const FooterButton = styled(GhostButton).attrs({
    $size: 'small' as const,
    type: 'button'
})`
    min-width: ${(props) => props.theme.app.component.button.small.height};
    width: ${(props) => props.theme.app.component.button.small.height};
    padding: 0;
    gap: 0;
`;

const ButtonIcon = styled.div<{ $isActive?: boolean; tooltip?: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    color: #263238;

    svg {
        width: 16px;
        height: 16px;
    }

    ${(props) =>
        props.$isActive &&
        `
            color: ${props.theme.app.secondary};
        `}
`;

interface CatalogListItemProps {
    model: CatalogListItemModel;
    selected: boolean;
    onClick: (hideAlreadyConfiguredBadge?: boolean) => void;
    onCatalogListItemButtonClick: (button: CatalogListItemButton, model: CatalogListItemModel) => void;
    hideAlreadyConfiguredBadge?: boolean;
    disableProductCode?: string;
}

const AlreadyImportedBadge = styled(Badge).attrs({ variant: 'info' as const })`
    position: absolute;
    top: 0;
    left: 0;
    background-color: color-mix(in srgb, #99cf16 70%, white);
`;

// Item component
export const CatalogListItem: FC<CatalogListItemProps> = ({
    model,
    selected,
    onClick,
    onCatalogListItemButtonClick,
    hideAlreadyConfiguredBadge,
    disableProductCode
}) => {
    let imagePreview: React.ReactNode = <img src={NoImagePng} alt='' style={{ opacity: 0.5 }} />;

    if (model.thumbnail) imagePreview = <img loading={'lazy'} src={model.thumbnail} alt={model.title || ''} />;

    if (model.customThumbnailComponent) imagePreview = model.customThumbnailComponent;

    const isAlreadyConfigured = model.isConfiguratedInZakeke && !hideAlreadyConfiguredBadge;
    const isNotSelectable = model.id === disableProductCode || isAlreadyConfigured;

    return (
        <Container
            role={model.readOnly ? 'group' : 'button'}
            aria-label={model.title}
            aria-disabled={model.id === disableProductCode}
            aria-pressed={model.readOnly ? undefined : selected}
            onClick={!model.readOnly && !isNotSelectable ? () => onClick(hideAlreadyConfiguredBadge) : undefined}
            $readOnly={model.readOnly}
            $isDisabled={model.id === disableProductCode}
            $isNotSelectable={isAlreadyConfigured}
        >
            {model.isConfiguratedInZakeke && !hideAlreadyConfiguredBadge && <AlreadyImportedBadge>{T._('Already Configured', 'Admin')}</AlreadyImportedBadge>}
            {!model.isConfiguratedInZakeke}

            <ImageContainer $isDisabled={model.id === disableProductCode}>{imagePreview}</ImageContainer>

            {model.customFooter ? (
                model.customFooter
            ) : (
                <CatalogListItemFooter>
                    <CatalogListItemFooterText $isConfiguratedInZakeke={model.isConfiguratedInZakeke} $hideAlreadyConfiguredBadge={hideAlreadyConfiguredBadge}>
                        <span>{cutString(model.title, 40)}</span>
                        <span>{model.subtitle}</span>
                    </CatalogListItemFooterText>

                    <FooterButtons role='toolbar' aria-label='Catalog item actions'>
                        {model.buttons?.map((x) => {
                            {
                                return (
                                    <FooterButton
                                        key={x.key}
                                        aria-label={x.tooltip || `Action button for ${model.title}`}
                                        aria-disabled={!x.isActive}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onCatalogListItemButtonClick(x, model);
                                        }}
                                    >
                                        <ButtonIcon tooltip={x.tooltip} $isActive={x.isActive} aria-hidden='true'>
                                            {x.icon}
                                        </ButtonIcon>
                                    </FooterButton>
                                );
                            }
                        })}
                    </FooterButtons>
                </CatalogListItemFooter>
            )}
            {selected && (
                <CheckIcon aria-hidden='true'>
                    <MdCheckCircle />
                </CheckIcon>
            )}
        </Container>
    );
};
